import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { type ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../Button/Button.tsx';
import { DEFAULT_LIST_BULK_ADD_FOOTER_HEIGHT, ITEM_SPLITTER_REGEX } from './constants.ts';
import {
  StyledActionButtonsStack,
  StyledContainerStack,
  StyledFooterButtonStack,
  StyledInputStack,
  StyledTextField,
} from './ListBulkAddFooter.styled.ts';
import { ListBulkAddFooterError } from './ListBulkAddFooterError.tsx';
import { getInputPlaceholder } from './utils.ts';

// ------

export type BulkAddValidationResult<T> = {
  acceptedItems: T[];
  error: string;
  rejectedItems: string[];
} & BulkAddValidationItemsExtractor<T>;

// ---

type BulkAddValidationItemsExtractor<T> = T extends string
  ? BulkAddValidationStringResult
  : BulkAddValidationGenericResult<T>;

type BulkAddValidationGenericResult<T> = {
  acceptedItemsStringExtractor: (item: T) => string;
};

type BulkAddValidationStringResult = {
  acceptedItemsStringExtractor?: never;
};

// ------

export type ListBulkAddFooterProps<T> = {
  isDisabled?: boolean;
  isInitiallyOpen?: boolean;
  height?: number;
  onBulkAdd: (items: T[]) => void;
  onValidate: (items: string[]) => Promise<BulkAddValidationResult<T>>;
};

export const ListBulkAddFooter = <T,>({
  height = DEFAULT_LIST_BULK_ADD_FOOTER_HEIGHT,
  isDisabled = false,
  isInitiallyOpen = false,
  onBulkAdd,
  onValidate,
}: ListBulkAddFooterProps<T>) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isOpen, setIsOpen] = useState<boolean>(isInitiallyOpen);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [validationResult, setValidationResult] = useState<BulkAddValidationResult<T> | null>(null);
  const [values, setValues] = useState<string>('');
  const { t } = useTranslation();

  const handleBulkAdd = () => {
    onBulkAdd(validationResult?.acceptedItems || []);

    setIsOpen(false);
    setIsValid(false);
    setValidationResult(null);
    setValues('');
  };

  const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
    setIsValid(false);
    setValues(event.target.value);
  };

  const handleValidation = async () => {
    setIsValidating(true);

    const items = values
      .split(ITEM_SPLITTER_REGEX)
      .map(value => value.trim())
      .filter(value => !!value);

    try {
      const validationResult = await onValidate(items);
      setValidationResult(validationResult);

      const { acceptedItems, acceptedItemsStringExtractor } = validationResult;
      const validItemsString = acceptedItems.map(item => acceptedItemsStringExtractor?.(item) || item).join('\n');

      setValues(validItemsString);
      setIsValid(!!acceptedItems.length);
    } catch (_err) {
      enqueueSnackbar({ message: t('Input validation failed.'), variant: 'error' });
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <>
      {isOpen && (
        <StyledContainerStack height={height}>
          <StyledActionButtonsStack>
            <Button onClick={() => setIsOpen(false)} startIcon={<CloseIcon />} variant="text">
              {t('Close')}
            </Button>
          </StyledActionButtonsStack>

          <StyledInputStack>
            {!!validationResult?.rejectedItems.length && <ListBulkAddFooterError {...validationResult} />}

            <StyledTextField
              data-test="bulk-placeholder"
              disabled={isDisabled || isValidating}
              fullWidth
              label={t('Bulk Add')}
              multiline
              slotProps={{ input: { disableUnderline: true } }}
              placeholder={getInputPlaceholder(t)}
              onChange={handleInput}
              value={values}
            />
          </StyledInputStack>

          <StyledActionButtonsStack hasMarginTop>
            <Button
              data-test="bulk-add-button"
              color="primary"
              disabled={isDisabled || !isValid || isValidating}
              onClick={handleBulkAdd}
            >
              {t('Add')}
            </Button>

            <Button
              data-test="bulk-validate-button"
              disabled={isDisabled || isValidating || values.length === 0}
              loading={isValidating}
              onClick={handleValidation}
            >
              {t('Validate')}
            </Button>
          </StyledActionButtonsStack>
        </StyledContainerStack>
      )}

      <StyledFooterButtonStack>
        <Button
          color="primary"
          disabled={isDisabled}
          onClick={() => setIsOpen(true)}
          startIcon={<AddIcon />}
          variant="text"
          data-test="bulk-add-open-button"
        >
          {t('Bulk add')}
        </Button>
      </StyledFooterButtonStack>
    </>
  );
};
